import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid, page } from './common.js';
/** Module `nfc-cards` (docs/modules/nfc-cards.md, ADR 0024). Owner: task S34. */
export const NfcCardStatus = z.enum(['active', 'blocked', 'lost', 'retired']);
export const NfcCard = z.object({
    id: Uuid,
    memberId: Uuid,
    memberName: z.string(),
    label: z.string(),
    /** Last 4 hex characters of the UID as typed or read, for recognising a card; the UID itself is not stored. */
    hint: z.string(),
    status: NfcCardStatus,
    blockReason: z.enum(['lost', 'stolen', 'other']).nullable(),
    registeredVia: z.enum(['portal', 'import', 'kiosk']),
    lastUsedAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
});
/** UID as hex (separators allowed; normalised by the shared nfcUidNormalise helper). */
export const NfcUid = z.string().trim().min(8).max(40).regex(/^[0-9A-Fa-f:\- ]+$/);
export const NfcCardCreate = z.object({ memberId: Uuid, uid: NfcUid, label: z.string().trim().max(60).default('') });
export const NfcCardUpdate = z.object({ label: z.string().trim().max(60).optional() });
export const NfcCardBlock = z.object({ reason: z.enum(['lost', 'stolen', 'other']) });
export const NfcCardListQuery = PageQuery.extend({ memberId: Uuid.optional(), status: z.enum(['active', 'blocked', 'lost', 'retired', 'all']).default('all') });
export const NfcCardList = page(NfcCard);
/** Enrolment on a kiosk (ADR 0024 "On the kiosk"). */
export const NfcEnrolmentCreate = z.object({ deviceId: Uuid, memberIds: z.array(Uuid).min(1).max(200) });
export const NfcEnrolment = z.object({
    id: Uuid,
    deviceId: Uuid,
    status: z.enum(['open', 'done', 'cancelled', 'expired']),
    expiresAt: IsoDateTime,
    items: z.array(z.object({ memberId: Uuid, memberName: z.string(), cardId: Uuid.nullable(), error: z.string().nullable() })),
});
export const NfcEnrolTapResult = z.object({ ok: z.boolean(), errorKey: z.string().nullable(), nextMemberId: Uuid.nullable() });
/** Kiosk config section `nfc-cards`. Keys are HMAC(siteNfcKey, uidKey) hex (ADR 0024). */
export const KioskNfcSection = z.object({
    siteNfcKey: z.string(),
    cards: z.record(z.string(), Uuid),
    blocked: z.array(z.string()),
    /** groupId -> needs PIN after the tap. */
    requirePin: z.record(z.string(), z.boolean()),
    /** Kiosk event for a blocked card tapped (no sign-in). */
    alertOnBlockedUse: z.boolean(),
});
export const NfcCardsGroupSettings = z.object({
    enabled: z.boolean().default(true),
    requirePin: z.boolean().default(false),
    maxCardsPerMember: z.number().int().min(1).max(5).default(5),
});
export const NfcCardsSiteSettings = z.object({
    alertOnBlockedUse: z.boolean().default(true),
});
/** POST /nfc-cards/import: the CSV text (columns: member email or id, UID, label; a header row is skipped). Added by S34. */
export const NfcCardImport = z.object({ csv: z.string().min(1).max(1_000_000) });
export const NfcCardImportReport = z.object({
    total: z.number().int().min(0),
    added: z.number().int().min(0),
    /** Row numbers are 1-based as in the file; reasons are i18n keys, never the UID. */
    skipped: z.array(z.object({ row: z.number().int().min(1), reasonKey: z.string() })),
});
//# sourceMappingURL=nfc-cards.js.map